/**
 * Client-side gameplay configuration.
 * All tunable values live here so designers can adjust without touching game logic.
 * Values that must match the server (PLAYER_SPEED, TILE_SIZE, etc.) are imported
 * from @game/shared and re-exported for convenience.
 */

import { PLAYER_SPEED, WALK_SPEED, HURRY_SPEED, MOVE_ACCEL_TIME, MOVE_DECEL_TIME, HURRY_BLEND_TIME, TILE_SIZE, TICK_RATE, WORLD_WIDTH, WORLD_HEIGHT, LOOT_PICKUP_RANGE, LOOT_DESPAWN_BLINK_MS } from '@game/shared';

// ── Re-exports from shared (server-authoritative values) ──
export { PLAYER_SPEED, WALK_SPEED, HURRY_SPEED, MOVE_ACCEL_TIME, MOVE_DECEL_TIME, HURRY_BLEND_TIME, TILE_SIZE, TICK_RATE, WORLD_WIDTH, WORLD_HEIGHT, LOOT_PICKUP_RANGE, LOOT_DESPAWN_BLINK_MS };

// ── Camera ──
/** Lerp factor for camera smoothing (0 = no follow, 1 = instant snap) */
export const CAMERA_LERP = 0.1;
/** Deadzone width/height — camera won't move until player exceeds this region */
export const CAMERA_DEADZONE_WIDTH = 100;
export const CAMERA_DEADZONE_HEIGHT = 100;

// ── Dynamic camera (POI zoom) ──
/** Default camera zoom level (normal gameplay) */
export const CAMERA_ZOOM_DEFAULT = 1.0;
/** Zoom level when fully inside a POI's inner radius (< 1 = zoom out) */
export const CAMERA_ZOOM_POI = 0.75;
/** Distance (cartesian px) at which POI zoom begins to take effect */
export const CAMERA_POI_OUTER_RADIUS = 400;
/** Distance (cartesian px) at which POI zoom reaches full strength */
export const CAMERA_POI_INNER_RADIUS = 150;
/** Lerp speed for zoom transitions (0–1, applied frame-rate-independently) */
export const CAMERA_ZOOM_LERP_SPEED = 0.08;

// ── Dynamic camera (travel zoom) ──
/** Zoom level when travelling at speed for a sustained period (< 1 = zoom out) */
export const CAMERA_ZOOM_TRAVEL = 0.65;
/** Zoom level when the player slows down / stops (10% closer than default) */
export const CAMERA_ZOOM_SETTLED = 1.1;
/** Minimum speed (cartesian px/s) to count as "travelling" — set above WALK_SPEED
 *  so travel zoom only kicks in during hurry pace (AGE-1137) */
export const CAMERA_TRAVEL_SPEED_THRESHOLD = 110;
/** Seconds of sustained movement before travel zoom kicks in */
export const CAMERA_TRAVEL_ONSET_SECS = 2.5;
/** Seconds of low movement before zoom settles back in */
export const CAMERA_SETTLE_ONSET_SECS = 1.0;
/** Lerp speed for travel zoom transitions (slower than POI for cinematic feel) */
export const CAMERA_TRAVEL_ZOOM_LERP_SPEED = 0.04;
/** Super zoom level when approaching a POI during sustained travel —
 *  much further out than either travel or POI alone to capture scale */
export const CAMERA_ZOOM_TRAVEL_POI = 0.45;
/** Absolute minimum camera zoom — prevents the camera from pulling out
 *  further than this regardless of POI / travel / combined influences. */
export const CAMERA_ZOOM_MIN = 0.20;

// ── Dynamic camera (home entry settle-zoom) ──
/** Brief intimate zoom on home entry before settling to default (AGE-1178) */
export const CAMERA_ZOOM_HOME_ENTER = 1.1;
/** Duration (seconds) of the home entry settle ease (1.1 → 1.0) */
export const CAMERA_HOME_SETTLE_SECS = 1.5;

// ── Expedition door POI overrides (AGE-1178) ──
// Per-realm zoom / radius values for dungeon-gate POIs so the camera
// widens differently for each realm's expedition doors.
export const EXPEDITION_DOOR_POI_OVERRIDES: Record<string, { zoom: number; outerRadius: number; innerRadius: number }> = {
  verdana:   { zoom: 0.55, outerRadius: 600, innerRadius: 200 },
  sablewind: { zoom: 0.50, outerRadius: 500, innerRadius: 180 },
  frostmere: { zoom: 0.50, outerRadius: 550, innerRadius: 200 },
  ember:     { zoom: 0.40, outerRadius: 700, innerRadius: 250 },
};

// ── Player visuals ──
export const PLAYER_COLORS = [
  0x3a5a9a, 0xb82e3c, 0x7ec86a, 0xd46a2a,  // Blue(18), Red(11), LightGreen(16), Orange(12)
  0x6a3a8a, 0x2a7a6a, 0xe8c44a, 0x3a1a4a,  // Purple(21), Teal(22), Yellow(13), DarkPurple(20)
];
export const LOCAL_PLAYER_ALPHA = 1.0;
export const REMOTE_PLAYER_ALPHA = 0.8;
export const PLAYER_LABEL_FONT_SIZE = '11px';
export const PLAYER_LABEL_COLOR = '#d4d4e4'; // 4 — Off-White

// ── Interpolation ──
/** Time window (ms) over which remote players interpolate to server position */
export const REMOTE_INTERPOLATION_WINDOW_MS = 100;
/** Distance threshold (in tiles) before snapping local player to server position */
export const SERVER_RECONCILIATION_SNAP_TILES = 2;
/** Lerp speed for smooth reconciliation (fraction per frame at 60fps baseline) */
export const SERVER_RECONCILIATION_LERP = 0.15;

// ── Enemy visuals ──
export const ENEMY_COLOR = 0xb82e3c; // 11 — Red
export const ENEMY_ALPHA = 0.9;
export const ENEMY_LABEL_COLOR = '#b82e3c'; // 11 — Red
/** Alpha for dead enemy corpses (fades out before despawn) */
export const ENEMY_DEAD_ALPHA = 0.3;

// ── Grid ──
export const GRID_LINE_COLOR = 0x2b2b3d; // 1 — Dark Gray
export const GRID_LINE_ALPHA = 0.4;

// ── Connection ──
export const CONNECTION_RETRY_DELAY_MS = 3000;
export const CONNECTION_STATUS_FADE_DELAY_MS = 2000;
export const CONNECTION_STATUS_FADE_DURATION_MS = 1000;

// ── Combat visuals ──
//
// v2 Phase U combat retune (GDD §11.2): player light swing cycles on a ~1.5s
// cadence, heavy on ~2.5s. Swing timing itself is server-authoritative and
// lives in `@game/shared` (LIGHT_ATTACK_COOLDOWN_MS / HEAVY_ATTACK_COOLDOWN_MS,
// COMBO_CHAIN, COMBO_RECOVERY_MS, COMBO_SINGLE_RECOVERY_MS). These visual
// constants are the client-side flourishes around each landed hit; they are
// tuned to feel deliberate alongside the slower swings, not to match exact
// cycle lengths.

/** Duration (ms) to display the attack hitbox visual.
 *  v2 Phase U: doubled from 150ms so the hit flash feels as deliberate as
 *  the swing that produced it. */
export const ATTACK_HITBOX_DISPLAY_MS = 300;
/** Colors for combo steps (attack1, attack2, attack3) */
export const COMBO_HITBOX_COLORS = [0xe8c44a, 0xd46a2a, 0xb82e3c]; // Yellow(13), Orange(12), Red(11)
/** Alpha for attack hitbox visual */
export const ATTACK_HITBOX_ALPHA = 0.4;
/** Duration (ms) to display combo step text */
export const COMBO_TEXT_DISPLAY_MS = 600;
/** Combo text colors per step */
export const COMBO_TEXT_COLORS = ['#e8c44a', '#d46a2a', '#b82e3c']; // Yellow(13), Orange(12), Red(11)
/** Combo text labels per step */
export const COMBO_TEXT_LABELS = ['Hit 1', 'Hit 2', 'Hit 3!'];

// ── Invulnerability visuals ──
/** Duration (ms) per blink cycle during invulnerability */
export const INVULNERABILITY_BLINK_INTERVAL_MS = 150;
/** Alpha when blinking "off" during invulnerability */
export const INVULNERABILITY_BLINK_ALPHA_MIN = 0.2;
/** Alpha when blinking "on" during invulnerability */
export const INVULNERABILITY_BLINK_ALPHA_MAX = 1.0;

// ── Loot visuals ──
/** Duration (ms) per blink cycle when loot is about to despawn */
export const LOOT_DESPAWN_BLINK_INTERVAL_MS = 150;
/** Duration (ms) for pickup floating text animation */
export const LOOT_PICKUP_TEXT_DURATION_MS = 1200;
/** Pixels the pickup text floats upward */
export const LOOT_PICKUP_TEXT_RISE_PX = 40;

// ── Weapon buff visuals ──
/** Threshold (ms) remaining on weapon buff before pulse warning */
export const WEAPON_BUFF_PULSE_THRESHOLD_MS = 5000;
/** Pulse animation speed (ms) when buff is about to expire */
export const WEAPON_BUFF_PULSE_INTERVAL_MS = 500;

// ── Game feel: Screen shake ──
/** Shake intensity (fraction of viewport) for player-received hits */
export const SHAKE_INTENSITY_PLAYER_HIT = 0.008;
/** Shake duration (ms) for player-received hits */
export const SHAKE_DURATION_PLAYER_HIT_MS = 120;
/** Shake intensity for heavy enemy attacks (boss slams, etc.) */
export const SHAKE_INTENSITY_HEAVY = 0.015;
/** Shake duration (ms) for heavy attacks */
export const SHAKE_DURATION_HEAVY_MS = 200;
/** Shake intensity for boss phase transitions */
export const SHAKE_INTENSITY_BOSS_PHASE = 0.025;
/** Shake duration (ms) for boss phase transitions */
export const SHAKE_DURATION_BOSS_PHASE_MS = 350;

// ── Game feel: Hit freeze (hitstop) ──
/** Duration (ms) of the freeze frame on successful melee hit */
export const HITSTOP_DURATION_MS = 60;
/** Duration (ms) of hitstop for heavy attacks */
export const HITSTOP_HEAVY_DURATION_MS = 100;
/** Duration (ms) of hitstop for combo finisher (hit 3) */
export const HITSTOP_COMBO_FINISHER_DURATION_MS = 120;

// ── Game feel: Particle effects ──
/** Number of spark particles on enemy hit */
export const HIT_SPARK_COUNT = 6;
/** Hit spark particle lifetime (ms) */
export const HIT_SPARK_LIFESPAN_MS = 300;
/** Hit spark particle speed range (px/s) */
export const HIT_SPARK_SPEED_MIN = 80;
export const HIT_SPARK_SPEED_MAX = 200;
/** Hit spark particle size (px) */
export const HIT_SPARK_SIZE = 3;
/** Hit spark color */
export const HIT_SPARK_COLOR = 0xe8c44a; // 13 — Yellow

/** Number of death puff particles */
export const DEATH_PUFF_COUNT = 10;
/** Death puff particle lifetime (ms) */
export const DEATH_PUFF_LIFESPAN_MS = 500;
/** Death puff particle speed range (px/s) */
export const DEATH_PUFF_SPEED_MIN = 30;
export const DEATH_PUFF_SPEED_MAX = 120;
/** Death puff particle size (px) */
export const DEATH_PUFF_SIZE = 4;
/** Death puff color */
export const DEATH_PUFF_COLOR = 0x8b8ba8; // 3 — Light Gray

/** Number of loot pickup sparkle particles */
export const LOOT_SPARKLE_COUNT = 8;
/** Loot sparkle particle lifetime (ms) */
export const LOOT_SPARKLE_LIFESPAN_MS = 600;
/** Loot sparkle particle speed range (px/s) */
export const LOOT_SPARKLE_SPEED_MIN = 40;
export const LOOT_SPARKLE_SPEED_MAX = 100;
/** Loot sparkle particle size (px) */
export const LOOT_SPARKLE_SIZE = 3;
/** Loot sparkle color */
export const LOOT_SPARKLE_COLOR = 0xe8c44a;

/** Number of heal particles */
export const HEAL_PARTICLE_COUNT = 10;
/** Heal particle lifetime (ms) */
export const HEAL_PARTICLE_LIFESPAN_MS = 700;
/** Heal particle speed range (px/s) */
export const HEAL_PARTICLE_SPEED_MIN = 20;
export const HEAL_PARTICLE_SPEED_MAX = 60;
/** Heal particle size (px) */
export const HEAL_PARTICLE_SIZE = 3;
/** Heal particle color */
export const HEAL_PARTICLE_COLOR = 0x7ec86a;

/** Number of melee swing trail particles */
export const SWING_TRAIL_COUNT = 5;
/** Swing trail particle lifetime (ms) */
export const SWING_TRAIL_LIFESPAN_MS = 200;
/** Swing trail particle size (px) */
export const SWING_TRAIL_SIZE = 2;
/** Swing trail color */
export const SWING_TRAIL_COLOR = 0xd4d4e4; // 4 — Off-White

/** Number of boss phase transition particles */
export const BOSS_PHASE_BURST_COUNT = 20;
/** Boss phase burst particle lifetime (ms) */
export const BOSS_PHASE_BURST_LIFESPAN_MS = 800;
/** Boss phase burst particle speed range (px/s) */
export const BOSS_PHASE_BURST_SPEED_MIN = 60;
export const BOSS_PHASE_BURST_SPEED_MAX = 250;
/** Boss phase burst particle size (px) */
export const BOSS_PHASE_BURST_SIZE = 5;
/** Boss phase burst color */
export const BOSS_PHASE_BURST_COLOR = 0xb82e3c; // 11 — Red

// ── Ability visuals ──
/** Shield overlay color */
export const SHIELD_OVERLAY_COLOR = 0x2a7a6a; // 22 — Teal — shield/defense
/** Shield overlay alpha */
export const SHIELD_OVERLAY_ALPHA = 0.25;
/** Dash trail ghost color */
export const DASH_TRAIL_COLOR = 0x6a9ad4; // 19 — Light Blue — dodge trail
